import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router';
import { ScrollArea } from '../components/ui/scroll-area';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <StockHeroStrip stockKey="coupleOutdoor" alt="Illustrative stock imagery" />
        <div className="rounded-xl border border-violet-100/90 bg-white/95 p-8 shadow-lg">
          <h1 className="text-3xl font-light mb-2">Privacy Policy</h1>
          <p className="text-sm text-neutral-500 mb-8">Last updated: March 24, 2026</p>

          <ScrollArea className="h-[calc(100vh-240px)]">
            <div className="space-y-6 pr-4">
              <section>
                <h2 className="text-xl font-medium mb-3">Our privacy commitment</h2>
                <p className="text-neutral-700 mb-3">
                  Relationship data is sensitive. TENSION is designed so your day-to-day insights—check-ins,
                  tension score patterns (RTI), and journal-style notes—can stay on <strong>your device</strong> by default. We do not
                  monetize your personal data, we do not sell it, and we do not use it for advertising.
                </p>
                <p className="text-neutral-700">
                  This policy explains what we collect, why we collect it, where it lives, and what you can control. If
                  anything here conflicts with what you need, please contact us before using the product.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">1. How we think about privacy</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Local-first by default:</strong> Core relationship tracking data is intended to remain on your
                    device in the browser (unless you turn on optional cloud sync or export data yourself).
                  </li>
                  <li>
                    <strong>Minimal collection:</strong> We do not ask for access to your messages, email inbox, or
                    third-party social accounts to run the core product.
                  </li>
                  <li>
                    <strong>Transparency:</strong> We list data categories below so you can make informed choices.
                  </li>
                  <li>
                    <strong>Security in depth:</strong> Connections use TLS (HTTPS). Account credentials are handled by
                    Supabase Auth; passwords are hashed; payments are processed by Stripe—we never store full card numbers
                    on our servers.
                  </li>
                  <li>
                    <strong>What “encryption” means here:</strong> We use industry-standard transport encryption (TLS)
                    for data moving between your device and our services. Optional device-side encryption or secure
                    storage may be available depending on your platform and settings. This is{' '}
                    <strong>not</strong> the same as “end-to-end encrypted” messaging between two people (like some chat
                    apps). We use precise language so you know exactly what is protected and how.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">2. Where data lives (at a glance)</h2>
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                  <table className="w-full text-sm text-left text-neutral-700">
                    <thead className="bg-neutral-50 text-neutral-900">
                      <tr>
                        <th className="p-3 font-medium">Category</th>
                        <th className="p-3 font-medium">Typical location</th>
                        <th className="p-3 font-medium">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      <tr>
                        <td className="p-3">Check-ins, tension scores (RTI), relationship notes</td>
                        <td className="p-3">Your device (browser storage)</td>
                        <td className="p-3">Core product experience; stays with you unless you export or enable sync</td>
                      </tr>
                      <tr>
                        <td className="p-3">Account email, auth session, subscription status</td>
                        <td className="p-3">Supabase (managed backend)</td>
                        <td className="p-3">Sign-in, account recovery, billing entitlements</td>
                      </tr>
                      <tr>
                        <td className="p-3">Payment details</td>
                        <td className="p-3">Stripe</td>
                        <td className="p-3">We rely on Stripe; we do not store full card numbers on our servers</td>
                      </tr>
                      <tr>
                        <td className="p-3">Operational analytics &amp; admin tooling</td>
                        <td className="p-3">Supabase-backed services (including key-value storage for operational keys)</td>
                        <td className="p-3">
                          Running the app, security, support, and aggregated insights—{' '}
                          <strong>not</strong> a substitute for your private journal on-device
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-neutral-700 mt-3 text-sm">
                  If you enable optional cloud sync, your data may be stored in our backend systems so it can be
                  retrieved on your other devices. That data is protected with access controls and TLS. Treat sync as a
                  deliberate convenience choice.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">3. Information we collect</h2>

                <h3 className="font-medium mt-4 mb-2">Information you provide</h3>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Account information:</strong> Email address, name, password (handled by Supabase Auth; we do
                    not store plaintext passwords)
                  </li>
                  <li>
                    <strong>Check-ins and personalization:</strong> Tension score (RTI) responses, emotional states, attachment
                    context, optional partner identifiers
                  </li>
                  <li>
                    <strong>Support &amp; feedback:</strong> Messages you send us when you contact support
                  </li>
                </ul>

                <h3 className="font-medium mt-4 mb-2">Automatically collected</h3>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Device &amp; app:</strong> Device type, OS, browser, app version (for compatibility and
                    debugging)
                  </li>
                  <li>
                    <strong>Usage &amp; diagnostics:</strong> Feature usage, session duration, error logs—used to improve
                    stability and product quality
                  </li>
                  <li>
                    <strong>Network:</strong> IP address and timestamps (standard for security and abuse prevention)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">4. How we use your information</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>Provide tension score tracking, insights, and action plans</li>
                  <li>Maintain your account, authentication, and subscription status</li>
                  <li>Process payments and prevent fraud</li>
                  <li>Provide customer support when you contact us</li>
                  <li>Improve reliability, security, and product performance</li>
                  <li>Comply with legal obligations when required</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">5. Data storage and security</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Local-first:</strong> Sensitive relationship tracking data is designed to remain on your
                    device unless you export it or use optional sync.
                  </li>
                  <li>
                    <strong>Encryption in transit:</strong> We use TLS (HTTPS) between your device and our services.
                  </li>
                  <li>
                    <strong>Supabase:</strong> Authentication and backend data are hosted on Supabase infrastructure with
                    provider-standard protections.
                  </li>
                  <li>
                    <strong>Payments:</strong> Handled by Stripe; subject to Stripe&apos;s security and compliance
                    programs.
                  </li>
                  <li>
                    <strong>Access controls:</strong> We limit internal access to operational systems on a need-to-know
                    basis for running the service.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">6. Data sharing and disclosure</h2>
                <p className="text-neutral-700 mb-3">
                  <strong>We do not sell your personal information.</strong>
                </p>
                <p className="text-neutral-700 mb-3">We may share limited data with service providers who help us operate the product:</p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Infrastructure &amp; auth:</strong> Supabase (database, authentication, storage)
                  </li>
                  <li>
                    <strong>Payments:</strong> Stripe
                  </li>
                  <li>
                    <strong>Email / transactional delivery:</strong> When we send emails (e.g., account or security
                    messages), we use providers that process messages on our behalf
                  </li>
                  <li>
                    <strong>Legal:</strong> If required by law, court order, or to protect rights and safety
                  </li>
                  <li>
                    <strong>Business transfers:</strong> In a merger or acquisition, users will be notified if personal
                    data is transferred
                  </li>
                </ul>
                <p className="text-neutral-700 mt-3">
                  We require service providers to use data only to provide services to us and to protect it appropriately.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">7. Your privacy rights</h2>
                <p className="text-neutral-700 mb-3">Depending on where you live, you may have rights to:</p>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>
                    <strong>Access:</strong> Request information about data we hold about you
                  </li>
                  <li>
                    <strong>Correction:</strong> Update account information in Settings where available
                  </li>
                  <li>
                    <strong>Deletion:</strong> Delete your account and associated server-side data where applicable
                    (Settings → Delete Account, where offered)
                  </li>
                  <li>
                    <strong>Portability:</strong> Export your data where the product provides export tools
                  </li>
                  <li>
                    <strong>Opt-out:</strong> Disable non-essential analytics where settings allow
                  </li>
                </ul>
                <p className="text-neutral-700 mt-3">
                  We design our practices to align with common expectations under frameworks like GDPR and CCPA, but
                  your specific rights depend on your jurisdiction. Contact us to exercise a request.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">8. Data retention</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                  <li>Local data remains until you clear app storage or uninstall the browser data</li>
                  <li>Account data is retained until you delete your account</li>
                  <li>After deletion, server-side data may take a short period to purge from backups</li>
                  <li>Aggregated analytics may be retained longer in a non-identifiable form</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">9. Cookies and tracking</h2>
                <p className="text-neutral-700">
                  We use cookies and similar technologies needed for authentication, session management, and basic
                  analytics. We do not use third-party advertising cookies to sell your data.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">10. Children&apos;s privacy</h2>
                <p className="text-neutral-700">
                  TENSION is not intended for anyone under 18. We do not knowingly collect personal information from
                  children.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">11. International users</h2>
                <p className="text-neutral-700">
                  If you access TENSION from outside your home country, your information may be processed in the United
                  States or other countries where our providers operate. We use appropriate safeguards as required by
                  applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">12. Changes to this policy</h2>
                <p className="text-neutral-700">
                  We may update this Privacy Policy. Material changes will be communicated via email or in-app notice
                  where reasonable. Continued use after changes means you accept the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium mb-3">13. Contact us</h2>
                <p className="text-neutral-700">
                  For privacy questions or to exercise your rights, contact us at:
                </p>
                <ul className="list-none pl-0 mt-2 space-y-1 text-neutral-700">
                  <li>Email: privacy@tensionapp.com</li>
                  <li>In-app: Settings → Help &amp; Support → Contact Us</li>
                </ul>
              </section>

              <div className="tension-glow mt-8 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-900">
                  <strong>Privacy is a product decision:</strong> We treat relationship data as high-sensitivity. If you
                  believe we can do better—or if you need help exporting or deleting your data—reach out. We are
                  committed to responding in good faith.
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
